import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { logError } from "@/lib/logger";
import { useAuth } from "@/context/AuthContext";

const WishlistContext = createContext(null);

export function WishlistProvider({ children }) {
  const { user } = useAuth();
  const [ids, setIds] = useState(new Set());

  const refresh = useCallback(async () => {
    if (!user) {
      setIds(new Set());
      return;
    }
    try {
      const { data } = await api.get("/wishlist");
      setIds(new Set(data.ids));
    } catch (e) {
      logError("Wishlist load failed", e);
    }
  }, [user]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const has = useCallback((id) => ids.has(id), [ids]);

  const toggle = useCallback(
    async (product) => {
      if (!user) {
        toast.error("Please sign in to save favourites", {
          action: { label: "Sign in", onClick: () => (window.location.href = "/account") },
        });
        return;
      }
      const id = product.id;
      const wasSaved = ids.has(id);
      // optimistic update
      setIds((prev) => {
        const next = new Set(prev);
        wasSaved ? next.delete(id) : next.add(id);
        return next;
      });
      try {
        if (wasSaved) {
          await api.delete(`/wishlist/${id}`);
          toast.success("Removed from favourites");
        } else {
          await api.post(`/wishlist/${id}`);
          toast.success("Saved to favourites");
        }
      } catch (e) {
        logError("Wishlist toggle failed", e);
        toast.error("Could not update favourites");
        setIds((prev) => {
          const next = new Set(prev);
          wasSaved ? next.add(id) : next.delete(id);
          return next;
        });
      }
    },
    [user, ids]
  );

  return (
    <WishlistContext.Provider value={{ ids, count: ids.size, has, toggle, refresh }}>
      {children}
    </WishlistContext.Provider>
  );
}

export const useWishlist = () => useContext(WishlistContext);
